// the roulette: wheels of options, each slice sized by its weight so the wheel shows the real odds.
// shared by the server (which keeps each person's wheels) and the app (which spins them)

export interface Slice {
  id: string;
  label: string;
  // how likely it is next to the others, 0.1 to 100
  weight: number;
  // "" follows the theme, otherwise one of SLICE_COLOURS
  colour: string;
  emoji: string;
  // knocked out by an elimination spin, back when the wheel is reset
  out: boolean;
}

export interface Spin {
  label: string;
  at: string;
}

export type WheelMode = "keep" | "knockout";
export type SpinLength = "quick" | "normal" | "long";

export interface Wheel {
  id: string;
  name: string;
  slices: Slice[];
  mode: WheelMode;
  length: SpinLength;
  sound: boolean;
  history: Spin[];
  updatedAt: string;
}

export interface RouletteState {
  wheels: Wheel[];
}

export const MAX_SLICES = 60;
export const MAX_WHEELS = 40;
export const MAX_HISTORY = 40;
export const MIN_WEIGHT = 0.1;
export const MAX_WEIGHT = 100;

// fixed picks next to the theme's own colours
export const SLICE_COLOURS: Record<string, string> = {
  red: "#ff4458",
  orange: "#ff8a3d",
  yellow: "#ffd23f",
  lime: "#b8f55a",
  green: "#3ddc97",
  teal: "#2ec4b6",
  cyan: "#4de8ff",
  blue: "#4d7cff",
  violet: "#a99bff",
  purple: "#8b3dff",
  pink: "#ff6fb5",
  white: "#f2ecdf",
  black: "#15161c"
};

export const SPIN_SECONDS: Record<SpinLength, number> = { quick: 2.6, normal: 5.2, long: 8.5 };

export interface Preset {
  name: string;
  slices: Array<[string, string?, number?]>;
}

export const PRESETS: Preset[] = [
  { name: "yes or no", slices: [["yes", "👍"], ["no", "👎"]] },
  { name: "what to draw", slices: [["gesture warm up", "✍️"], ["portrait", "🙂"], ["an oc", "⭐"], ["hands and feet", "✋"], ["background", "🏙️"], ["comic page", "📖"], ["fanart", "💖"], ["redraw an old piece", "🔁"]] },
  { name: "dinner", slices: [["pizza", "🍕"], ["sushi", "🍣"], ["curry", "🍛"], ["burgers", "🍔"], ["noodles", "🍜"], ["cook something", "🍳"]] },
  { name: "dice", slices: [["1"], ["2"], ["3"], ["4"], ["5"], ["6"]] },
  { name: "who does it", slices: [["fractal", "🎨"], ["pauline", "🌸"]] }
];

export function today(): string {
  return new Date().toISOString();
}

function cleanId(value: unknown, fallback: () => string): string {
  const text = String(value || "").replace(/[^\w-]/g, "").slice(0, 40);
  return text || fallback();
}

export function cleanWeight(value: unknown): number {
  const number = Number(value);
  if (!Number.isFinite(number)) return 1;
  return Math.round(Math.min(MAX_WEIGHT, Math.max(MIN_WEIGHT, number)) * 10) / 10;
}

// one emoji or a couple of characters, never a paragraph
export function cleanEmoji(value: unknown): string {
  const text = String(value || "").trim();
  return Array.from(text).slice(0, 4).join("");
}

export function cleanSlice(raw: Partial<Slice> | undefined, newId: () => string): Slice | null {
  const label = String(raw?.label || "").replace(/\s+/g, " ").trim().slice(0, 80);
  if (!label) return null;
  return {
    id: cleanId(raw?.id, newId),
    label,
    weight: cleanWeight(raw?.weight ?? 1),
    colour: raw?.colour && SLICE_COLOURS[String(raw.colour)] ? String(raw.colour) : "",
    emoji: cleanEmoji(raw?.emoji),
    out: raw?.out === true
  };
}

export function cleanWheel(raw: Partial<Wheel> | undefined, newId: () => string): Wheel {
  const seen = new Set<string>();
  const slices = (Array.isArray(raw?.slices) ? raw!.slices : [])
    .map((slice) => cleanSlice(slice, newId))
    .filter((slice): slice is Slice => {
      if (!slice || seen.has(slice.id)) return false;
      seen.add(slice.id);
      return true;
    })
    .slice(0, MAX_SLICES);
  const history = (Array.isArray(raw?.history) ? raw!.history : [])
    .map((spin) => ({ label: String(spin?.label || "").slice(0, 80), at: String(spin?.at || "").slice(0, 30) }))
    .filter((spin) => spin.label && spin.at)
    .slice(0, MAX_HISTORY);
  return {
    id: cleanId(raw?.id, newId),
    name: String(raw?.name || "").replace(/\s+/g, " ").trim().slice(0, 60) || "new wheel",
    slices,
    mode: raw?.mode === "knockout" ? "knockout" : "keep",
    length: raw?.length === "quick" || raw?.length === "long" ? raw.length : "normal",
    sound: raw?.sound !== false,
    history,
    updatedAt: String(raw?.updatedAt || today()).slice(0, 30)
  };
}

// the slices still on the wheel
export function liveSlices(wheel: Wheel): Slice[] {
  return wheel.slices.filter((slice) => !slice.out);
}

export function totalWeight(slices: Slice[]): number {
  return slices.reduce((sum, slice) => sum + slice.weight, 0);
}

// "pizza *3" or "pizza x3" sets a weight while typing a list
export function parseEntry(line: string): { label: string; weight: number } | null {
  const match = line.trim().match(/^(.*?)(?:\s*\*\s*(\d+(?:\.\d+)?)|\s+[x×](\d+(?:\.\d+)?))?$/i);
  const label = (match?.[1] || "").trim();
  const weight = match?.[2] || match?.[3];
  if (!label) return null;
  return { label, weight: weight ? cleanWeight(weight) : 1 };
}

// a fair pick: a uniform number from the given random source, landing on a slice in proportion to its weight
export function pickWeighted<T extends { weight: number }>(items: T[], unit: number): number {
  const total = items.reduce((sum, item) => sum + item.weight, 0);
  if (!items.length || total <= 0) return -1;
  let left = unit * total;
  for (let at = 0; at < items.length; at++) {
    left -= items[at].weight;
    if (left < 0) return at;
  }
  return items.length - 1;
}
